// =============================================================================
// Constants for the guided agent-creation (provisioning) wizard. See
// AVR-435. The wizard provisions the *data half* of a voice agent
// (tenant -> master row -> Cartesia agent_id validation -> env config); the
// *code half* (tool router + cartesia deploy, keyed by slug) is a separate
// engineer step and is NOT covered here.
// =============================================================================

// Wizard step order; index doubles as the "Step N of M" position. The Cartesia
// agent comes first — it's the real-world starting point (the agent already
// exists on Cartesia's dashboard), and its tenant list isn't org-scoped so it
// can precede org selection. The agent's org + its own `ai_agent` user are
// provisioned next; the final Agent-details step atomically provisions the
// voice_agents row + its config in one transaction (POST /voice-agents/provision).
export const STEP = {
  CARTESIA: 0,
  ORGANIZATION: 1,
  AGENT_USER: 2,
  AGENT_ROW: 3,
}

export const STEP_LABELS = ['Cartesia agent', 'Organization', 'Agent identity', 'Agent details']
export const TOTAL_STEPS = STEP_LABELS.length

// For an agent with its own backend code, the slug is the one its code calls —
// usually its ROUTER_NAME env var, operator-supplied (Cartesia's API never
// exposes the value, so neither side can derive it). Checked client-side for
// shape only; the BE checks it's released here (422 slug_not_deployed,
// overridable after a confirm), free (409 slug_taken) and not a generated slug
// (422 slug_reserved_shape) at provision time. That last check needs the BE's
// word lists, so it is not repeated here. An agent without backend code sends
// no slug at all: the BE derives one from the prompt-config seed's agent id, so
// the pattern is never applied on that path (AVR-837).
export const SLUG_PATTERN = /^[a-z0-9-]+$/

// Default organization a new agent belongs to (Balto). Org 1 is the Balto
// super-admin org by convention across the app.
export const DEFAULT_ORG_ID = 1

// The prompt-config key ("<customer>/<agent>", e.g. athena/customer_service) an
// optional seeded agent_prompt_configs row is stored under (AVR-559). This is
// the PE agent_id the agent's prompt_config.yaml declares — NOT the Cartesia
// agent_id. Shape-checked client-side; the BE re-validates and rejects a
// duplicate with 409 prompt_config_exists.
export const PE_AGENT_ID_PATTERN = /^[a-z0-9_-]+\/[a-z0-9_-]+$/

// Discriminators the BE returns on validate (step 1) and provision (step 4); the
// fetch layer carries `kind` on both 409 and 422. Kinds nothing keys on (e.g.
// slug_reserved_shape) render their BE detail in the step's inline error block.
export const ERROR_KIND = {
  AGENT_ID_IN_USE: 'agent_id_in_use',
  // 409 on the No path: this prompt config ID was provisioned before.
  PROMPT_CONFIG_EXISTS: 'prompt_config_exists',
  // 422: the typed slug has no code router released on this environment. The
  // operator may confirm and resubmit with allow_unreleased_slug (AVR-836).
  SLUG_NOT_DEPLOYED: 'slug_not_deployed',
}

// Where the engineer creates the Cartesia agent (the multi-step seam: they
// leave here, make the agent, and return with its agent_id). TODO(AVR-435):
// confirm the exact dashboard URL with the BE/Cartesia owner.
export const CARTESIA_DASHBOARD_URL = 'https://play.cartesia.ai/agents'
